import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { Locale } from '../i18n';
import { widgetText } from './copy';
import { MUTED_TEXT_CLASS } from './styles';

export interface CuentaAtrasDeVueltaProps {
  /** Segundos que se esperan antes de volver solo. */
  segundos: number;
  locale: Locale;
  /** Que hacer al agotarse. Se invoca UNA sola vez. */
  onFinalizar: () => void;
}

/**
 * Cuenta atras para volver a la tienda sin que el comprador tenga que pulsar nada.
 *
 * <p>Es la unica pieza del widget que actua SOLA, y por eso lleva tres frenos:
 *
 * <ul>
 *   <li><strong>Cualquier interaccion la cancela, y para siempre.</strong> Un temporizador que se
 *       lleva la pagina mientras alguien lee el monto o copia la referencia es hostil. En cuanto
 *       el comprador toca una tecla o el puntero, manda el comprador. Es ademas lo que evita que
 *       esto sea un limite de tiempo del que no se pueda salir (WCAG 2.2.1).</li>
 *   <li><strong>Se anuncia.</strong> `aria-live="polite"` para que un lector de pantalla diga que
 *       la pagina va a cambiar sola, sin robar el foco.</li>
 *   <li><strong>Muere con el componente.</strong> Sin la limpieza, salir de la pantalla dejaria un
 *       temporizador vivo que navega despues, desde otro sitio.</li>
 * </ul>
 *
 * <p>Quien decide CUANDO se usa esto es {@code StatusScreen}: solo en el desenlace bueno, solo en
 * modo pagina y solo si el comercio declaro a donde volver.
 */
export function CuentaAtrasDeVuelta({
  segundos,
  locale,
  onFinalizar,
}: CuentaAtrasDeVueltaProps): JSX.Element | null {
  const [restantes, setRestantes] = useState<number>(segundos);
  const [cancelada, setCancelada] = useState<boolean>(false);

  /*
   * El callback va por referencia y no en las dependencias del efecto: quien lo pasa suele
   * construirlo en linea, asi que cambia de identidad en cada render y reiniciaria el temporizador
   * una y otra vez. La cuenta atras nunca llegaria a cero.
   */
  const finalizarRef = useRef(onFinalizar);
  useEffect(() => {
    finalizarRef.current = onFinalizar;
  }, [onFinalizar]);

  useEffect(() => {
    if (cancelada) {
      return undefined;
    }
    function cancelar(): void {
      setCancelada(true);
    }
    document.addEventListener('keydown', cancelar);
    document.addEventListener('pointerdown', cancelar);
    return () => {
      document.removeEventListener('keydown', cancelar);
      document.removeEventListener('pointerdown', cancelar);
    };
  }, [cancelada]);

  /*
   * UN solo `setInterval`, y no un `setTimeout` que se vuelve a armar en cada tick. Dos motivos, y
   * el segundo se descubrio probandolo:
   *
   *   1. Rearmar el temporizador desde un efecto acumula el retraso de cada render, asi que la
   *      cuenta se va quedando atras del reloj de pared.
   *   2. Encadenar timeouts hace que la cuenta dependa de que React re-renderice entre tick y
   *      tick. Con el reloj detenido de una prueba, adelantar ocho segundos de golpe solo
   *      disparaba UNO.
   *
   * El intervalo no depende de `restantes` --si dependiera, volveria a rearmarse en cada tick y
   * estariamos en el mismo sitio--, asi que el corte en cero vive en el actualizador.
   */
  useEffect(() => {
    if (cancelada) {
      return undefined;
    }
    const id = setInterval(() => {
      setRestantes((quedan) => (quedan <= 0 ? 0 : quedan - 1));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [cancelada]);

  // Avisar EXACTAMENTE una vez: el intervalo sigue latiendo en cero hasta que el padre desmonte
  // esta pantalla, y sin el pestillo se navegaria en cada latido.
  const yaAviso = useRef(false);
  useEffect(() => {
    if (cancelada || restantes > 0 || yaAviso.current) {
      return;
    }
    yaAviso.current = true;
    finalizarRef.current();
  }, [restantes, cancelada]);

  if (cancelada) {
    return null;
  }

  return (
    <p
      data-testid="auto-return-countdown"
      role="status"
      aria-live="polite"
      className={MUTED_TEXT_CLASS}
    >
      {widgetText(locale, 'status.auto_return').replace('{segundos}', String(restantes))}
    </p>
  );
}
